import { expectAtIndex } from "@aprao/common/src/array/expectAtIndex.js";
import {
  type BigFraction,
  bigFractionFromFraction,
  bigFractionFromNumber,
} from "@aprao/common/src/math/BigFraction.js";
import {
  FRACTION_ZERO,
  fractionLt,
  fractionMin,
} from "@aprao/common/src/math/fraction.js";
import type { Fraction } from "@aprao/common/src/shapes/common.js";

import type { SiteUseClass } from "../shapes/ShapeSitePurchase.js";

interface StampDutyBandDescriptor {
  lowerBound: BigFraction;
  rate: BigFraction;
}

export const bandByUseClasses: Record<
  SiteUseClass,
  ReadonlyArray<StampDutyBandDescriptor>
> = {
  Residential: [
    {
      lowerBound: FRACTION_ZERO,
      rate: bigFractionFromFraction("0/100" as Fraction),
    },
    {
      lowerBound: bigFractionFromNumber(125000),
      rate: bigFractionFromFraction("2/100" as Fraction),
    },
    {
      lowerBound: bigFractionFromNumber(250000),
      rate: bigFractionFromFraction("5/100" as Fraction),
    },
    {
      lowerBound: bigFractionFromNumber(925000),
      rate: bigFractionFromFraction("10/100" as Fraction),
    },
    {
      lowerBound: bigFractionFromNumber(1500000),
      rate: bigFractionFromFraction("12/100" as Fraction),
    },
  ],
  "Residential.2022-09-23": [
    {
      lowerBound: FRACTION_ZERO,
      rate: bigFractionFromFraction("0/100" as Fraction),
    },
    {
      lowerBound: bigFractionFromNumber(250000),
      rate: bigFractionFromFraction("5/100" as Fraction),
    },
    {
      lowerBound: bigFractionFromNumber(925000),
      rate: bigFractionFromFraction("10/100" as Fraction),
    },
    {
      lowerBound: bigFractionFromNumber(1500000),
      rate: bigFractionFromFraction("12/100" as Fraction),
    },
  ],
  "Residential.2025-04-01": [
    {
      lowerBound: FRACTION_ZERO,
      rate: bigFractionFromFraction("0/100" as Fraction),
    },
    {
      lowerBound: bigFractionFromNumber(125_000),
      rate: bigFractionFromFraction("2/100" as Fraction),
    },
    {
      lowerBound: bigFractionFromNumber(250_000),
      rate: bigFractionFromFraction("5/100" as Fraction),
    },
    {
      lowerBound: bigFractionFromNumber(925_000),
      rate: bigFractionFromFraction("10/100" as Fraction),
    },
    {
      lowerBound: bigFractionFromNumber(1_500_000),
      rate: bigFractionFromFraction("12/100" as Fraction),
    },
  ],
  NonResidential: [
    {
      lowerBound: FRACTION_ZERO,
      rate: bigFractionFromFraction("0/100" as Fraction),
    },
    {
      lowerBound: bigFractionFromNumber(150000),
      rate: bigFractionFromFraction("2/100" as Fraction),
    },
    {
      lowerBound: bigFractionFromNumber(250000),
      rate: bigFractionFromFraction("5/100" as Fraction),
    },
  ],
};

export const calculateStampDuty = (
  price: BigFraction,
  bands: ReadonlyArray<StampDutyBandDescriptor>,
  surcharge: BigFraction | null,
) => {
  let stampDuty = FRACTION_ZERO;

  // this works only if the bands are sorted by lower bound ascending value
  for (let bandIndex = 0; bandIndex < bands.length; bandIndex++) {
    const band = expectAtIndex(bands, bandIndex);
    const isLastBand = bandIndex === bands.length - 1;

    const nextBandLowerBound =
      isLastBand ? price : expectAtIndex(bands, bandIndex + 1).lowerBound;

    if (fractionLt(band.lowerBound, price)) {
      const taxableAmount = fractionMin(price, nextBandLowerBound).sub(
        band.lowerBound,
      );

      stampDuty = stampDuty.add(
        taxableAmount.mul(band.rate.add(surcharge ?? FRACTION_ZERO)),
      );
    } else {
      // stop when price is below the current band's lower bound
      break;
    }
  }

  return stampDuty;
};
